import styles from './TicketTypeDetailsForm.module.css';

import { Option } from 'features/common/components/Form/types';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';
import { PunchupNumericInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupNumericInput';
import { PunchupTextArea } from 'features/common/components/Punchup/primitives/forms/fields/PunchupTextArea';
import { PunchupFormSelect } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupSelect';
import { Pool } from '../../types';
import { Tag } from 'features/common/types';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupDateTimePicker } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupDateTimePicker/PunchupDateTimePicker';
import { PunchupFormToggle } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFormToggle';
import { TicketTagsField } from '../TicketTagsField';
import { PunchupColorPicker } from 'features/common/components/Punchup/primitives/forms/pickers/PunchupColorPicker';
import { PunchupFieldLabel } from 'features/common/components/Punchup/primitives/forms/fields/PunchupFieldLabel';
import { convertUTCToDate } from 'utils/utils';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PunchupImageUpload } from 'features/common/components/Punchup/composites/forms/PunchupImageUpload';
import { useOptionalAccordionSection } from 'features/common/components/Punchup/primitives/layout/PunchupAccordion';
import { PunchupDangerZone } from 'features/common/components/Punchup/composites/forms/PunchupDangerZone';
import { useParams } from 'react-router-dom';
import useGetEvent from 'features/common/hooks/useGetEvent';
import { useTicketTypeColors } from 'features/common/hooks/useTicketTypeColors';
import { useTicketTypeApi } from '../../useTicketTypeApi';
import { useTranslation } from 'react-i18next';
import { TicketDetailsParams } from '../../types';
import { SeatsCategories } from './SeatsCategories';
import { SeatsIOAvailabilityReport, SeatsIOCategory } from 'services/Seats';
import { useGetSeatsIOEvent } from 'hooks/useGetSeatsIOEvent';
import { useGetSeatsIOEventReport } from 'hooks/useGetSeatsIOEventReport';

// Total capacity of a seats.io category = sum of all object-status counts (available + booked +
// reserved + not_for_sale + disabled_by_social_distancing) under that category key. Returns
// undefined while the report is loading or when the category key is unknown.
const SEATS_REPORT_SECTIONS = [
	'disabled_by_social_distancing',
	'reservedByToken',
	'booked',
	'not_for_sale',
	'available',
] as const;

const getSeatsCategoryCapacity = (
	report: SeatsIOAvailabilityReport | undefined,
	categoryKey: string | undefined
): number | undefined => {
	if (!report || !categoryKey) return undefined;
	return SEATS_REPORT_SECTIONS.reduce((sum, key) => sum + (report[key]?.byCategoryKey?.[categoryKey] ?? 0), 0);
};

interface TicketTypeDetailsFormProps {
	isNewTicket?: boolean;
	/** Resolved ticket ID: savedTicketId after create, or the existing ticketId for edit. */
	resolvedTicketId?: number;
	/**
	 * Pre-fetched tags for this ticket type. Pass this when TicketTypeDetailsForm is rendered in
	 * a context where useTicketTypeApi cannot read the ticket ID from URL params (e.g. accordion
	 * inline form). When omitted, tags are sourced from useTicketTypeApi's ticket data.
	 */
	ticketTags?: Tag[];
	/**
	 * Tickets already sold for this tier. Same reason as `ticketTags`: in the accordion context
	 * `useTicketTypeApi` cannot read the ticket from URL params, so the orchestrator passes it. Drives
	 * the quantity floor + pool-membership lock once selling has started (docs/pools_business_logic.md T-4).
	 */
	soldCount?: number;
	/** When provided (and !isNewTicket), renders the Danger Zone at the bottom of the form. */
	onDelete?: () => void;
}

const TicketTypeDetailsForm: React.FC<TicketTypeDetailsFormProps> = ({
	isNewTicket = false,
	resolvedTicketId,
	ticketTags: propTicketTags,
	soldCount,
	onDelete,
}) => {
	const { t } = useTranslation();
	const { event } = useGetEvent();
	const { pools, isPoolsError, ticket } = useTicketTypeApi();
	const { watch, setValue } = useFormContext();
	// In the accordion this form saves under PunchupInlineEditor's overlay spinner, which covers the
	// image upload too; the sub-page path has no section and so no overlay. Reading the same isSaving
	// the overlay reads keeps one spinner visible when a save re-triggers the preview's own load.
	const accordionSection = useOptionalAccordionSection();
	const { id: eventId } = useParams<TicketDetailsParams>();

	const [initialLoad, setInitialLoad] = useState(true);
	const [previousPrivateLink, setPreviousPrivateLink] = useState(false);
	const [copiedToClipboard, setCopiedToClipboard] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

	const privateLink: boolean = watch('privateLink');
	const hiddenCode: string = watch('hiddenCode');
	const showImage: boolean = watch('showImage');
	const rawSeatsCategoryKey = watch('seatsCategoryKey');
	const seatsCategoryKey: string | undefined = rawSeatsCategoryKey ? String(rawSeatsCategoryKey) : undefined;
	const payWhatYouWant: boolean = watch('payWhatYouWant');
	const ticketColor: string = watch('color') || '';
	// When no colour is set, preview the stable palette colour this tier is shown with elsewhere (resolved
	// by its persisted name). View-only — picking is what commits a colour. Only for an existing tier.
	const { resolveByName } = useTicketTypeColors();
	const autoColor = !isNewTicket && ticket?.name ? resolveByName(ticket.name) : undefined;

	// Seated-event derivation. Reads from the same React Query keys as PunchupSeatsSection and
	// the SeatsCategories selector below — no extra fetches when the dashboard already loaded.
	const isSeated = !!event?.seatsEventKey;
	const { data: seatsEvent } = useGetSeatsIOEvent(isSeated ? event?.seatsEventKey : undefined);
	const { data: availabilityReport } = useGetSeatsIOEventReport(isSeated ? event?.seatsEventKey : undefined);
	const boundSeatsCategory: SeatsIOCategory | undefined = seatsEvent?.categories?.find(
		(c) => String(c.key) === seatsCategoryKey
	);
	const derivedCapacity = getSeatsCategoryCapacity(availabilityReport, seatsCategoryKey);

	const generateHexCode = (): string => {
		return Array.from({ length: 22 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
	};

	const getWidgetUrl = (code: string): string => {
		const widgetBase = process.env.REACT_APP_WIDGET;
		return `${widgetBase}/event/${eventId}/tickets?code=${code}`;
	};

	const handleCopyToClipboard = (text: string) => {
		// Try the in-iframe clipboard first; show "Copied!" only on a real success. The fallback posts to
		// the host (which copies on the parent frame, outside the iframe) — mirrors PunchupSeatsSection's
		// handleCopy and SeatsSection invariant 5. Posting blindly would assert a success we can't observe.
		const doCopy = () => window.parent.postMessage({ action: 'copy-to-clipboard', text }, '*');
		if (navigator.clipboard) {
			navigator.clipboard
				.writeText(text)
				.then(() => {
					setCopiedToClipboard(true);
					setTimeout(() => setCopiedToClipboard(false), 2000);
				})
				.catch(doCopy);
		} else {
			doCopy();
		}
	};

	const handleCategorySelect = (category: SeatsIOCategory) => {
		const categoryKeyStr = String(category.key);
		if (seatsCategoryKey === categoryKeyStr) {
			setValue('seatsCategoryKey', undefined);
		} else {
			setValue('seatsCategoryKey', categoryKeyStr);
		}
	};

	useEffect(() => {
		if (event?.endDate && event?.timeZone && !watch('offSellingTime')) {
			const offSellingTime = moment(convertUTCToDate(`${event.endDate}`, event.timeZone));
			setValue('offSellingTime', offSellingTime);
		}
	}, [event]);

	useEffect(() => {
		if (initialLoad && hiddenCode !== undefined) {
			setInitialLoad(false);
		}
	}, [hiddenCode, initialLoad]);

	/* why: PunchupFormToggle has no onChange seam, so previousPrivateLink emulates a transition handler —
	   fire the dependent setValues once, only on a genuine user toggle (not the initial seed). */
	useEffect(() => {
		if (!initialLoad && privateLink !== previousPrivateLink) {
			if (privateLink && !hiddenCode) {
				const newCode = generateHexCode();
				setValue('hiddenCode', newCode);
				setValue('ticketVisibility', false);
			} else if (!privateLink && hiddenCode) {
				setValue('hiddenCode', null);
			}
		}
		setPreviousPrivateLink(privateLink);
	}, [privateLink, hiddenCode, setValue, initialLoad, previousPrivateLink]);

	// "No pool" is an explicit, selectable default (poolId 0 → no tickets_pool_id sent). It replaces
	// PunchupFormSelect's anonymous "Select One…" so the un-pooled state reads clearly.
	const poolOptions: Option[] = [
		{ label: t('ticketDetails-pool-none', 'No pool'), value: 0 },
		...(pools?.map((pool: Pool) => ({ label: pool.name, value: pool.ID })) || []),
	];

	// Pool membership locks once a tier has started selling (T-4). The field keeps its dropdown and is
	// disabled — same control, same row, just not editable — so the manager still reads the current value.
	const effectiveSold = soldCount ?? ticket?.sold ?? 0;
	const poolLocked = effectiveSold > 0;

	// The locked field still has to say WHICH pool this tier sells from. Resolve the form's poolId against
	// the loaded pools; fall back to the pool whose member list contains this tier, because the single
	// ticket-type GET does not reliably carry the tickets_pool_id FK (the same backend gap the create flow
	// works around via addTicketTypeToPool).
	const assignedPoolId = Number(watch('poolId')) || 0;
	const memberTicketTypeId = resolvedTicketId ?? ticket?.ID;
	const assignedPool =
		pools?.find((pool: Pool) => pool.ID === assignedPoolId) ??
		pools?.find(
			(pool: Pool) => !!memberTicketTypeId && pool.ticketTypes?.some((member) => member.ID === memberTicketTypeId)
		);
	// A tier that HAS a pool must never read "No pool". "No pool" is a claim, and we may only make it when
	// the list loaded cleanly, the form carries no pool id, AND no loaded pool lists this tier as a member.
	// Everything we cannot resolve — list still in flight, fetch failed, an id whose pool is gone — renders
	// as the disabled select with NOTHING selected (displayValue null → the placeholder), so the field
	// states nothing rather than something false. Pending keeps the `…` this form already uses for a value
	// still on its way; the settled-but-unresolvable states show an em dash, which does not promise more.
	const poolsPending = pools === undefined && !isPoolsError;
	const poolUnresolvable = poolsPending || isPoolsError || assignedPoolId > 0;
	const lockedPoolValue: number | null = assignedPool ? assignedPool.ID : poolUnresolvable ? null : 0;

	// The sold floor lives in the field's help tooltip (not an inline caption) — appended with the live
	// count once some tickets have sold, so the row layout stays clean.
	const quantityHelp =
		t(
			'ticketDetails-quantity-help',
			'The total number of this ticket type available to sell. Once some have sold, you can raise this number but not lower it below what has already sold.'
		) +
		(effectiveSold > 0
			? ' ' +
			  t('ticketDetails-quantity-floor-hint', "{{count}} tickets are already sold, so the quantity can't go lower.", {
					count: effectiveSold,
			  })
			: '');

	return (
		<div className={styles.formFields}>
			{/* ── Section 1: Identity ──────────────────────────────────────────── */}
			<div className={styles.formSection}>
				<PunchupInput id="name" name="name" label={t('ticketDetails-ticket-type-name')} required />

				<PunchupTextArea id="description" name="description" label={t('ticketDetails-ticket-description')} />
			</div>

			{/* ── Section 2: Pricing & Availability ───────────────────────────── */}
			<div className={styles.formSection}>
				<PunchupFormToggle
					htmlFor="payWhatYouWant"
					name="payWhatYouWant"
					label={t('ticketDetails-pay-what-you-want', 'Pay what you want ticket')}
					info={t('ticketDetails-pay-what-you-want-info', 'Let attendees choose how much they pay for this ticket.')}
				/>

				<div className={styles.numericRow}>
					{payWhatYouWant ? (
						<>
							<PunchupNumericInput
								id="minPrice"
								name="minPrice"
								unit="money"
								label={t('ticketDetails-min-ticket-price')}
								min={0}
								step={0.01}
								required
							/>
							<PunchupNumericInput
								id="maxPrice"
								name="maxPrice"
								unit="money"
								label={t('ticketDetails-max-ticket-price')}
								min={0}
								step={0.01}
								required
							/>
						</>
					) : (
						<>
							<PunchupNumericInput
								id="initialPrice"
								name="initialPrice"
								unit="money"
								label={t('ticketDetails-ticket-price')}
								min={0}
								step={0.01}
								required
							/>
							<PunchupNumericInput
								id="percentageFee"
								name="percentageFee"
								unit="percent"
								label={t('ticketDetails-percentage-custom-fee-green')}
								min={0}
								help={t(
									'ticketDetails-percentage-custom-fee-help',
									"An extra charge added at checkout, set as a percentage of the ticket price. Leave it at zero if you don't want to add one."
								)}
							/>
							<PunchupNumericInput
								id="fixedFee"
								name="fixedFee"
								unit="money"
								label={t('ticketDetails-fixed-custom-fee-green')}
								min={0}
								step={0.01}
								help={t(
									'ticketDetails-fixed-custom-fee-help',
									"A flat dollar amount added to each ticket at checkout. Leave it at zero if you don't want to add one."
								)}
							/>
						</>
					)}
				</div>

				{/* Row 2 — the availability field that is NOT a stepper, alone on its row. A 48px select (editable
				    or disabled) and a 34px stepper can never share a baseline cleanly, so they no longer share a row.
				    Full-width, like every other single-field row in this form (name, description, the date pickers).
				    Seated shows have no pool at all — capacity comes from the seats.io chart — so the derived-capacity
				    readout takes the slot and keeps the Quantity → Max → Bundle reading order intact. */}
				{isSeated && (
					<div className={styles.derivedCapacityField} data-testid="derived-capacity">
						{/* Label sits ABOVE the box (like a normal field), not merged inside it. The box is a grey
						    read-only readout: the seats.io category colour dot + the derived capacity. */}
						<PunchupFieldLabel
							as="span"
							label={t('ticketDetails-quantity')}
							help={t(
								'ticketDetails-quantity-seated-help',
								"Capacity comes from the bound seats.io category and is managed in the seats chart — it can't be edited here."
							)}
						/>
						<div className={styles.derivedCapacityBox}>
							{boundSeatsCategory ? (
								<span className={styles.derivedCapacityValue}>
									<span className={styles.derivedCapacityColor} style={{ backgroundColor: boundSeatsCategory.color }} />
									{derivedCapacity !== undefined ? derivedCapacity : '…'}
								</span>
							) : (
								<span className={styles.derivedCapacityValue}>
									{t(
										'ticketDetails-quantity-derived-unbound',
										'Bind a seats.io category in the chart to set capacity.'
									)}
								</span>
							)}
						</div>
					</div>
				)}
				{/* One control in both states: the locked tier gets the SAME select, disabled, with its current
				    value preselected. The lock is explained by the field's help (the `?` beside the label), so
				    nothing has to be said inline. `displayValue` is display-only — the form value is never
				    written for the lock, so the readout can't dirty the form or change what Save sends. */}
				{!isSeated && (
					<PunchupFormSelect
						id="poolId"
						name="poolId"
						label={t('ticketDetails-pool-green')}
						options={poolOptions}
						disabled={poolLocked}
						displayValue={poolLocked ? lockedPoolValue : undefined}
						placeholder={poolLocked ? (poolsPending ? '…' : '—') : undefined}
						help={
							poolLocked
								? t(
										'ticketDetails-pool-locked-help',
										'Pool membership locks once a ticket type starts selling, so the shared-capacity accounting stays correct.'
								  )
								: t(
										'ticketDetails-pool-help',
										"Pools let several ticket types share one capacity, so they sell from the same pot of tickets. Choose No pool to keep this ticket's count on its own."
								  )
						}
					/>
				)}

				{/* Row 3 — the count steppers together. Same control, same height, so they line up by construction.
				    Seated shows drop Quantity here (it is the derived readout above) and keep the other two. */}
				<div className={styles.numericRow}>
					{!isSeated && (
						<PunchupNumericInput
							id="amount"
							name="amount"
							unit="count"
							showStepper
							label={t('ticketDetails-quantity')}
							min={Math.max(0, effectiveSold)}
							step={1}
							required
							help={quantityHelp}
						/>
					)}
					<PunchupNumericInput
						id="maxQuantity"
						name="maxQuantity"
						unit="count"
						showStepper
						clearable
						placeholder={t('ticketDetails-unlim', 'unlim')}
						label={t('ticketDetails-max-quantity-green')}
						min={1}
						step={1}
						help={t(
							'ticketDetails-max-quantity-help',
							'The most of this ticket one buyer can purchase in a single order. Leave it empty for no limit.'
						)}
					/>
					<PunchupNumericInput
						id="bundleSize"
						name="bundleSize"
						unit="count"
						showStepper
						clearable
						placeholder={t('ticketDetails-none', 'none')}
						label={t('ticketDetails-bundle-green')}
						min={1}
						step={1}
						help={t(
							'ticketDetails-bundle-help',
							'Sells this ticket in fixed groups, such as a pack of four. Buyers purchase one bundle at a time instead of single tickets. Leave it empty to sell single tickets.'
						)}
					/>
				</div>

				<PunchupDateTimePicker id="sellingTime" name="sellingTime" label={t('createEvent-tickets-onSale')} required />

				<PunchupDateTimePicker id="offSellingTime" name="offSellingTime" label={t('createEvent-tickets-offSale')} />
			</div>

			{/* ── Section 3: Additional ────────────────────────────────────────── */}
			<div className={styles.formSection}>
				<TicketTagsField
					ticketTags={propTicketTags ?? ticket?.tags}
					ticketTypeId={resolvedTicketId}
					isNewTicket={isNewTicket}
				/>

				{/* Ticket colour — sits beside the tags; tints the ticket-type tag shown on
				    Scanned Tickets, Attendee List, Comped/Transactions, and the analytics charts. */}
				<div className={styles.colorField}>
					<PunchupFieldLabel
						as="span"
						label={t('ticketDetails-ticket-color', 'Ticket color')}
						help={
							isSeated
								? t(
										'ticketDetails-ticket-color-help-seated',
										"A color used to label this ticket type across charts, attendee lists, and reports, so it's easy to tell apart at a glance. For seated shows, the dashboard uses the seating chart's color instead."
								  )
								: t(
										'ticketDetails-ticket-color-help',
										"A color used to label this ticket type across charts, attendee lists, and reports, so it's easy to tell apart at a glance."
								  )
						}
					/>
					<PunchupColorPicker
						value={ticketColor}
						autoColor={autoColor}
						onChange={(color) => setValue('color', color, { shouldDirty: true })}
					/>
				</div>

				<PunchupFormToggle
					htmlFor="ticketVisibility"
					name="ticketVisibility"
					label={t('ticketDetails-ticket-visbility')}
					info={t('ticketDetails-ticket-visbility-info')}
				/>

				<PunchupFormToggle
					htmlFor="alwaysShowTickets"
					name="alwaysShowTickets"
					label={t('ticketDetails-ticket-alwaysShowTickets')}
					info={t('ticketDetails-ticket-alwaysShowTickets-info')}
				/>

				<div>
					<PunchupFormToggle
						htmlFor="privateLink"
						name="privateLink"
						label={t('ticketDetails-ticket-privateLink')}
						info={t('ticketDetails-ticket-privateLink-info')}
					/>
					{privateLink && hiddenCode && (
						<div className={styles.widgetUrlContainer}>
							<label className={styles.widgetUrlLabel}>
								{t('ticketDetails-private-link-url-label', 'Tickets URL:')}
							</label>
							<div className={styles.widgetUrlWrapper}>
								<code className={styles.widgetUrl}>{getWidgetUrl(hiddenCode)}</code>
								<PunchupButton
									type="button"
									variant="secondary"
									text={
										copiedToClipboard
											? t('ticketDetails-copy-done', 'Copied!')
											: t('ticketDetails-copy', 'Copy to clipboard')
									}
									onClick={() => handleCopyToClipboard(getWidgetUrl(hiddenCode))}
								/>
							</div>
						</div>
					)}
				</div>

				<div>
					<PunchupFormToggle
						htmlFor="showImage"
						name="showImage"
						label={t('ticketDetails-ticket-showImage-green')}
						info={t('ticketDetails-ticket-showImage-info')}
					/>
					{showImage && (
						<PunchupImageUpload inputId="ticketImageStaged" embedded isSaving={accordionSection?.isSaving ?? false} />
					)}
				</div>

				<SeatsCategories
					eventKey={event?.seatsEventKey}
					chartKey={event?.seatsChartKey}
					selectedCategory={seatsCategoryKey}
					onCategorySelect={handleCategorySelect}
					readOnly={isSeated}
				/>
			</div>

			{!isNewTicket && onDelete && !isSeated && (
				<PunchupDangerZone
					title={t('ticketDetails-danger-zone-title', 'Danger Zone')}
					description={t('ticketDetails-danger-zone-description', 'Permanently delete this ticket type.')}
					warning={
						effectiveSold > 0
							? t('ticketDetails-danger-zone-warning', '{{count}} ticket(s) sold — all sales will become invalid.', {
									count: effectiveSold,
							  })
							: undefined
					}
					actions={
						showDeleteConfirm ? (
							<>
								<PunchupButton
									variant="destructive"
									type="button"
									onClick={onDelete}
									text={t('button-confirm', 'Confirm')}
								/>
								<PunchupButton
									variant="secondary"
									type="button"
									onClick={() => setShowDeleteConfirm(false)}
									text={t('button-cancel')}
								/>
							</>
						) : (
							<PunchupButton
								variant="destructive"
								type="button"
								onClick={() => setShowDeleteConfirm(true)}
								text={t('button-delete')}
							/>
						)
					}
				/>
			)}
		</div>
	);
};

export { TicketTypeDetailsForm };
